import { RotateCcw } from 'lucide-react';

import {
  DEFAULT_RESTAURANT_TABLE_THEME,
  restaurantTableThemeVars,
} from '../../../types/restaurant/RestaurantTableTypes';
import TableShapeIcon from '../tables/components/TableShapeIcon';
import {
  mapAppearanceSettings,
  toAppearanceUpdateDTO,
} from './RestaurantSettingsMapper';
import RestaurantSettingsFormLayout from './RestaurantSettingsFormLayout';
import { AppearanceSettingsForm } from './RestaurantSettingsTypes';
import useRestaurantSettingsForm from './useRestaurantSettingsForm';

interface Props {
  branchId: number;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

type ColorKey = Exclude<keyof AppearanceSettingsForm, 'active'>;

const COLOR_FIELDS: { key: ColorKey; label: string; help: string }[] = [
  {
    key: 'tableFillColor',
    label: 'Relleno de la mesa',
    help: 'Color del tablero de la mesa.',
  },
  {
    key: 'tableLineColor',
    label: 'Línea de la mesa',
    help: 'Color del borde de la mesa.',
  },
  {
    key: 'chairFillColor',
    label: 'Relleno de las sillas',
    help: 'Color del asiento de las sillas.',
  },
  {
    key: 'chairLineColor',
    label: 'Línea de las sillas',
    help: 'Color del contorno de las sillas.',
  },
];

const ColorRow = ({
  label,
  help,
  value,
  onChange,
}: {
  label: string;
  help: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const valid = HEX.test(value);
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-black dark:text-white">
        {label}
      </label>
      <div className="flex items-center gap-3 rounded-xl border border-stroke bg-gray-1 px-3 py-2.5 dark:border-strokedark dark:bg-meta-4">
        <input
          type="color"
          value={valid ? value : '#000000'}
          onChange={(event) => onChange(event.target.value.toLowerCase())}
          className="h-9 w-12 shrink-0 cursor-pointer border-0 bg-transparent p-0"
          aria-label={label}
        />
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          spellCheck={false}
          className="min-w-0 flex-1 bg-transparent font-mono text-sm uppercase text-black outline-none dark:text-white"
        />
      </div>
      <p className="mt-1 text-xs text-bodydark2 dark:text-bodydark">
        {valid ? help : 'Usa un color hexadecimal como #1C2434.'}
      </p>
    </div>
  );
};

const RestaurantAppearanceSettingsForm = ({ branchId }: Props) => {
  const settings = useRestaurantSettingsForm(
    branchId,
    'appearance',
    mapAppearanceSettings,
    toAppearanceUpdateDTO
  );
  const { form, updateField } = settings;

  const isDefault =
    form &&
    form.tableFillColor === DEFAULT_RESTAURANT_TABLE_THEME.tableFillColor &&
    form.tableLineColor === DEFAULT_RESTAURANT_TABLE_THEME.tableLineColor &&
    form.chairFillColor === DEFAULT_RESTAURANT_TABLE_THEME.chairFillColor &&
    form.chairLineColor === DEFAULT_RESTAURANT_TABLE_THEME.chairLineColor;

  return (
    <RestaurantSettingsFormLayout
      title="Apariencia de las mesas"
      description="Colores con los que se dibujan las mesas en la pantalla de Mesas y en el tablero de Órdenes."
      loading={settings.loading}
      saving={settings.saving}
      hasData={Boolean(form)}
      alert={settings.alert}
      onCloseAlert={() => settings.setAlert(null)}
      onSave={settings.save}
      onRetry={settings.reload}
    >
      {form && (
        <div className="grid items-start gap-6 xl:col-span-2 xl:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]">
          <div>
            <div className="grid gap-5 sm:grid-cols-2">
              {COLOR_FIELDS.map((field) => (
                <ColorRow
                  key={field.key}
                  label={field.label}
                  help={field.help}
                  value={form[field.key]}
                  onChange={(value) => updateField(field.key, value)}
                />
              ))}
            </div>

            <button
              type="button"
              disabled={Boolean(isDefault)}
              onClick={() => {
                updateField(
                  'tableFillColor',
                  DEFAULT_RESTAURANT_TABLE_THEME.tableFillColor
                );
                updateField(
                  'tableLineColor',
                  DEFAULT_RESTAURANT_TABLE_THEME.tableLineColor
                );
                updateField(
                  'chairFillColor',
                  DEFAULT_RESTAURANT_TABLE_THEME.chairFillColor
                );
                updateField(
                  'chairLineColor',
                  DEFAULT_RESTAURANT_TABLE_THEME.chairLineColor
                );
              }}
              className="mt-5 inline-flex items-center gap-2 rounded-lg border border-stroke px-3 py-2 text-sm font-medium text-bodydark2 transition hover:border-primary hover:text-primary disabled:opacity-40 dark:border-strokedark"
            >
              <RotateCcw className="h-4 w-4" />
              Restaurar valores por defecto
            </button>
          </div>

          <div
            className="rounded-2xl border border-stroke bg-gray-2/40 p-6 dark:border-strokedark dark:bg-meta-4/20 xl:sticky xl:top-4"
            style={restaurantTableThemeVars(form)}
          >
            <p className="text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-bodydark2">
              Vista previa
            </p>
            <div className="mt-4 flex flex-wrap items-end justify-center gap-6">
              <TableShapeIcon shape="SQUARE" capacity={4} className="h-20 w-20" />
              <TableShapeIcon shape="ROUND" capacity={4} className="h-20 w-20" />
              <TableShapeIcon
                shape="RECTANGLE"
                capacity={6}
                className="h-20 w-20"
              />
            </div>
            <p className="mx-auto mt-4 max-w-xs text-center text-xs text-bodydark2 dark:text-bodydark">
              Así se verán las mesas cuadradas, redondas y rectangulares en Mesas
              y en Órdenes.
            </p>
          </div>
        </div>
      )}
    </RestaurantSettingsFormLayout>
  );
};

export default RestaurantAppearanceSettingsForm;
